import { cn } from '@/utils/shad';
import { InfoIcon, LucideIcon } from 'lucide-react';
import React, { PropsWithChildren, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TooltipButton } from '@/comps/tooltip-button';
import { WithClassName, WithFormat } from '@/types/component';
import { BasicTooltip } from '@/comps/basic-tooltip';

type Props<T> = WithClassName & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Icon: LucideIcon | any;
  text: string;
  value?: T;
  onClick?: (value: T) => void;
  isActive: boolean;
  variant?: 'accent' | 'negative' | 'positive' | 'yellowv2' | 'purple';
  coloredIcon?: boolean;
  suffix?: React.ReactNode;
  helpText?: string;
  textClassName?: string;
  showTooltip?: boolean;
  tooltipText?: string;
  activeIndicatorClassname?: string;
  buttonClassName?: string;
};

export const NavElement = <T extends string>({
  Icon,
  text,
  isActive,
  onClick,
  value,
  variant = 'accent',
  suffix,
  helpText,
  className,
  textClassName,
  showTooltip,
  tooltipText,
  activeIndicatorClassname,
  buttonClassName,
}: Props<T>) => {
  const iconClass = useMemo(() => {
    if (isActive && variant === 'positive') return 'text-link';
    if (isActive && variant === 'negative') return 'text-negative';
    if (isActive && variant === 'accent') return 'text-accent-foreground';
    if (isActive && variant === 'yellowv2') return 'text-yellowv2';
    if (isActive && variant === 'purple') return 'text-icon';
    return '';
  }, [isActive, variant]);

  return (
    <BasicTooltip show={showTooltip} text={tooltipText ?? text}>
      <div className={cn('inline-block', className)} title={text}>
        <div className="relative mr-5 flex h-full min-h-12 items-center">
          <button
            onClick={() => typeof value !== 'undefined' && onClick?.(value)}
            className={cn(
              'px-0 text-[14px] font-medium text-muted-foreground hover:text-accent-foreground',
              { 'hover:cursor-auto': !onClick },
              buttonClassName,
            )}
          >
            <div
              className={cn(
                'flex h-6 items-center gap-1 rounded-[8px] px-1.5 py-1 text-[12px]',
                isActive && 'border',
              )}
            >
              {Icon && React.isValidElement(Icon) ? Icon : Icon ? <Icon size={12} className={cn(iconClass)} /> : null}
              <span
                className={cn(
                  isActive && 'text-accent-foreground',
                  'whitespace-nowrap hover:text-accent-foreground',
                  textClassName,
                )}
              >
                {text}
              </span>
              {suffix}
              {helpText && (
                <TooltipButton content={helpText}>
                  <InfoIcon className="ml-1 inline text-muted-foreground" size={12} />
                </TooltipButton>
              )}
            </div>
            {isActive && (
              <div
                className={cn('absolute right-0 bottom-0 left-0 h-px bg-accent-foreground', activeIndicatorClassname)}
              ></div>
            )}
          </button>
        </div>
      </div>
    </BasicTooltip>
  );
};
export type NavElementT<T> = {
  value: T;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Icon?: any;
  variant?: 'accent' | 'negative' | 'positive' | 'yellowv2' | 'purple';
  suffix?: React.ReactNode;
  render?: () => React.ReactNode;
};

type NavElementBarProps<T extends string> = WithClassName &
  PropsWithChildren &
  WithFormat & {
    activeValue?: T;
    setActiveValue?: (value: T) => void;
    items: NavElementT<T>[];
    textClassName?: string;
    showTooltip?: boolean;
    activeIndicatorClassname?: string;
    removeBorder?: boolean;
    buttonClassName?: string;
    scrollable?: boolean;
    showRightFade?: boolean;
  };

export const NavElementBar = <T extends string>({
  className,
  items,
  activeValue,
  setActiveValue,
  textClassName,
  showTooltip,
  format,
  activeIndicatorClassname,
  removeBorder,
  buttonClassName,
  scrollable = false,
  showRightFade = false,
  children,
}: NavElementBarProps<T>) => {
  const listRef = useRef<HTMLUListElement>(null);
  const endSentinelRef = useRef<HTMLLIElement>(null);
  const [showFade, setShowFade] = useState(false);
  const [scrollPaddingInline, setScrollPaddingInline] = useState({ left: '0px', right: '0px' });
  const itemsSignature = useMemo(() => items.map((item) => String(item.value)).join('|'), [items]);

  useLayoutEffect(() => {
    if (!scrollable) {
      setScrollPaddingInline({ left: '0px', right: '0px' });
      return;
    }

    const listElement = listRef.current;
    if (!listElement) return;

    const updateScrollPadding = () => {
      const computedStyles = window.getComputedStyle(listElement);
      setScrollPaddingInline((prev) => {
        const next = {
          left: computedStyles.paddingLeft,
          right: computedStyles.paddingRight,
        };
        if (prev.left === next.left && prev.right === next.right) {
          return prev;
        }
        return next;
      });
    };

    updateScrollPadding();

    const ro = new ResizeObserver(updateScrollPadding);
    ro.observe(listElement);

    return () => {
      ro.disconnect();
    };
  }, [className, scrollable]);

  useLayoutEffect(() => {
    if (!scrollable || !showRightFade) {
      setShowFade(false);
      return;
    }

    const listElement = listRef.current;
    const endSentinelElement = endSentinelRef.current;
    if (!listElement || !endSentinelElement) return;

    const updateFadeVisibility = () => {
      const hasOverflow = listElement.scrollWidth > listElement.clientWidth + 1;
      if (!hasOverflow) {
        setShowFade(false);
        return;
      }

      const listRect = listElement.getBoundingClientRect();
      const sentinelRect = endSentinelElement.getBoundingClientRect();
      const isSentinelVisible = sentinelRect.right <= listRect.right + 1 && sentinelRect.left >= listRect.left - 1;
      setShowFade(!isSentinelVisible);
    };

    updateFadeVisibility();

    const io = new IntersectionObserver(
      () => {
        updateFadeVisibility();
      },
      {
        root: listElement,
        threshold: 1,
      },
    );
    io.observe(endSentinelElement);

    const ro = new ResizeObserver(() => {
      updateFadeVisibility();
    });
    ro.observe(listElement);
    ro.observe(endSentinelElement);

    const mo = new MutationObserver(() => {
      updateFadeVisibility();
    });
    mo.observe(listElement, { childList: true, subtree: true, characterData: true });

    return () => {
      io.disconnect();
      ro.disconnect();
      mo.disconnect();
    };
  }, [itemsSignature, scrollable, showRightFade]);

  useEffect(() => {
    if (!scrollable) return;

    const listElement = listRef.current;
    const activeItem = listElement?.querySelector<HTMLLIElement>('[data-nav-element-active="true"]');
    if (!listElement || !activeItem) return;

    const previousItem = activeItem.previousElementSibling as HTMLLIElement | null;
    const computedStyles = window.getComputedStyle(listElement);
    const paddingLeft = parseFloat(computedStyles.paddingLeft) || 0;

    // Keep half of the previous tab visible to hint left-side scrollability.
    const leftPeek = previousItem ? previousItem.offsetWidth / 2 : 0;
    const targetLeft = Math.max(0, activeItem.offsetLeft - paddingLeft - leftPeek);

    listElement.scrollTo({ left: targetLeft, behavior: 'smooth' });
  }, [activeValue, scrollable]);

  const listElement = (
    <ul
      ref={listRef}
      className={cn(
        'flex h-12 items-center px-8',
        removeBorder ? '' : 'border-y',
        scrollable &&
          'no-scrollbar w-full min-w-0 touch-pan-x snap-x snap-proximity overflow-x-auto overflow-y-hidden scroll-smooth',
        className,
      )}
      style={
        scrollable
          ? {
              scrollPaddingLeft: scrollPaddingInline.left,
              scrollPaddingRight: scrollPaddingInline.right,
            }
          : undefined
      }
    >
      {items.map((item) => {
        return (
          <li
            key={item.value}
            data-nav-element-active={item.value === activeValue ? 'true' : undefined}
            className={cn(scrollable && 'shrink-0 snap-start')}
          >
            <NavElement
              isActive={item.value === activeValue}
              onClick={setActiveValue}
              value={item.value}
              Icon={item.Icon}
              text={format ? format(item.title) : item.title}
              variant={item.variant}
              suffix={item.suffix}
              textClassName={textClassName}
              showTooltip={showTooltip}
              tooltipText={item.title}
              activeIndicatorClassname={activeIndicatorClassname}
              buttonClassName={buttonClassName}
            />
          </li>
        );
      })}
      {children}
      {showRightFade && <li ref={endSentinelRef} aria-hidden className="h-px w-px shrink-0 snap-end" />}
    </ul>
  );

  if (!scrollable) {
    return listElement;
  }

  return (
    <div className="relative max-w-full overflow-hidden">
      {listElement}
      {showRightFade && showFade && (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-linear-to-l from-background via-background/80 to-transparent" />
      )}
    </div>
  );
};
